/*
 * Sportscraft: Nosto search results page.
 *
 * Alpine owns the chrome only (total, facets, chips, sort, pagination,
 * loading / empty / error states, URL sync). Product cards are never built
 * here: they are Liquid-rendered HTML injected as-is.
 *
 * Config comes from window.sportscraftNostoSearchConfig, emitted by
 * snippets/sportscraft-nosto-search.liquid. Store registration is order-independent -
 * see the note above the register() call at the bottom.
 */
(() => {
  const ENDPOINT = 'https://search.nosto.com/v1/graphql';
  const DEFAULT_TIMEOUT_MS = 2500;
  const CARD_TIMEOUT_MS = 8000;
  // sections/sportscraft-nosto-product-grid.liquid renders `limit: 20` handles and
  // silently drops the rest, so a bigger chunk size would lose cards.
  const TRANSPORT_HANDLE_LIMIT = 20;
  const CHUNK_RETRY_MS = 300;

  const shellElement = () => document.querySelector('[data-sportscraft-nosto-timeout]');

  const urlParam = (name) => new URLSearchParams(window.location.search).get(name);

  /*
   * The timeout is the "Response timeout" theme setting, carried on the shell as
   * data-sportscraft-nosto-timeout. ?sportscraft_nosto_timeout=<ms> overrides it for a single
   * page view, so the fallback path can be exercised without changing a store
   * setting.
   */
  const timeoutMs = () => {
    const override = Number(urlParam('sportscraft_nosto_timeout'));
    if (override > 0) return override;
    const shell = shellElement();
    const setting = shell ? Number(shell.getAttribute('data-sportscraft-nosto-timeout')) : 0;
    return setting > 0 ? setting : DEFAULT_TIMEOUT_MS;
  };

  // Support bypass: ?sportscraft_nosto=off leaves Shopify's own grid exactly as the
  // server rendered it. Liquid cannot read the query string (the request object
  // exposes design_mode, host, locale, origin and page_type only), so the switch
  // has to live here rather than in the section.
  const bypassed = () => urlParam('sportscraft_nosto') === 'off';

  // Backstop for the reveal gate: longer than the Nosto timeout plus the card
  // requests, so it only ever fires when the render failed to settle at all.
  const gateBackstopMs = () => timeoutMs() + CARD_TIMEOUT_MS + 1500;

  // #collection is the anchor the native pagination links target, so this lands
  // in the same place; the theme's own scroll-padding-top clears the sticky
  // header. Reduced-motion form copied from the theme's anchor handler
  // (assets/theme.js).
  const scrollToResults = () => {
    const anchor = document.getElementById('collection');
    if (!anchor) return;
    anchor.scrollIntoView({
      block: 'start',
      behavior: window.matchMedia('(prefers-reduced-motion: no-preference)').matches ? 'smooth' : 'auto'
    });
  };

  // Terms facets ride in the query string as nf.<field>=v1,v2 and the price
  // range as nprice=<min>-<max>. NOT filter.* / price: Shopify's native
  // faceting owns filter.* on the /search route and a matching key makes
  // Shopify re-run its own search server-side (measured: filter.v.availability=1
  // moved the native result count from 35 to 33).
  const FACET_PARAM_PREFIX = 'nf.';
  const PRICE_PARAM = 'nprice';
  const PRICE_FIELD = 'price';
  // Availability is pulled out of the generic facet loop and driven by the same
  // toggle native uses (snippets/facets.liquid). Nosto's value is schema.org.
  const AVAILABILITY_FIELD = 'availability';
  const AVAILABILITY_VALUE = 'InStock';

  const config = () => window.sportscraftNostoSearchConfig || {};

  // Nosto search analytics: fire-and-forget, never allowed to throw into the search path.
  const track = (fn) => { if (typeof window.nostojs !== 'function') return; try { window.nostojs((api) => { try { fn(api); } catch (e) {} }); } catch (e) {} };

  // One-shot marker the drawer sets on a keyword click.
  const takeKeyword = () => { try { const keyword = sessionStorage.getItem('sportscraftNostoKeyword'); sessionStorage.removeItem('sportscraftNostoKeyword'); return keyword; } catch (e) { return null; } };

  // Handle of a successful quick add started in `container`; the quick-buy modal moves to <body>, so it is traced via its opener.
  const addedHandle = (event, container) => {
    const form = event.target;
    if (!form || !form.closest) return null;
    const card = form.closest('product-card[handle]');
    if (card) return container.contains(card) ? card.getAttribute('handle') : null;
    const modal = form.closest('quick-buy-modal');
    if (!modal || !(modal.controls || []).some((control) => container.contains(control))) return null;
    return handleFromUrl(modal.getAttribute('product-url'));
  };

  /* ---------------------------------------------------------------- marks */

  const metrics = { runs: [], last: null };
  window.sportscraftNostoMetrics = metrics;

  const startRun = (size) => {
    const run = {
      size: size,
      nostoMs: null,
      cardsMs: null,
      cardRequests: 0,
      cardsFromCache: 0,
      placeholdersCaught: 0,
      cardsFromServer: 0,
      cardsInserted: 0,
      cardsMissing: 0,
      chunkFailures: [],
      discarded: false,
      promosInserted: 0,
      // the GraphQL string actually sent, so what went on the wire is readable
      query: null,
      startedAt: performance.now()
    };
    metrics.last = run;
    metrics.runs.push(run);
    return run;
  };

  /* ------------------------------------------------------------ card cache */

  /*
   * Keyed rendering: a card's HTML only depends on its handle, so a sort or a
   * page change that returns handles already seen must not refetch them.
   * outerHTML is cached rather than the node, because a node moved out of the
   * grid loses its place; each reuse is reparsed into a fresh element.
   * Bounded at three pages' worth of handles, evicted oldest-first.
   */
  const cardCache = new Map();
  const cacheOrder = [];

  const cacheLimit = () => Math.max(60, 3 * (Number(config().size) || 24));

  const cachePut = (handle, entry) => {
    if (!cardCache.has(handle)) cacheOrder.push(handle);
    cardCache.set(handle, entry);
    while (cacheOrder.length > cacheLimit()) {
      const evicted = cacheOrder.shift();
      cardCache.delete(evicted);
    }
  };

  const cardFromCache = (handle) => {
    const entry = cardCache.get(handle);
    if (!entry) return null;
    // wrapped in <ul><li> so the parse tree matches the query below AND so the
    // parser does not relocate non-<li> children out of a bare <ul>
    const doc = new DOMParser().parseFromString('<ul><li>' + entry.card + entry.extras.join('') + '</li></ul>', 'text/html');
    const card = doc.querySelector('product-card');
    if (!card || !card.getAttribute('handle')) return null;
    const extras = [...doc.querySelectorAll('li > *')].filter((el) => el !== card);
    return { card: card, extras: extras };
  };

  /* -------------------------------------------------- injected card wiring */

  /*
   * product-card.liquid renders TWO things per product: the <product-card>
   * itself, and - deliberately outside it, so it never sits inside the card's
   * anchor - the per-card "Save to wishlist" drawer. This renderer used to keep
   * only the <product-card>, so the heart's overlay button
   * (snippets/sportscraft-card-wishlist-trigger.liquid) called
   * document.getElementById(<drawer id>).show() on a drawer that had never been
   * injected, and silently did nothing. The drawer cannot go in the grid - it
   * would become a grid cell - so it is parked in a container at body level,
   * which is where a fixed-position drawer wants to be anyway.
   */
  const extrasHost = () => {
    let host = document.getElementById('sportscraft-nosto-card-extras');
    if (!host) {
      host = document.createElement('div');
      host.id = 'sportscraft-nosto-card-extras';
      document.body.appendChild(host);
    }
    return host;
  };

  const injectExtras = (extras) => {
    const host = extrasHost();
    for (const element of extras) {
      const id = element.getAttribute && element.getAttribute('id');
      // drawer ids carry the product id, so re-rendering the same product must
      // not stamp a duplicate id into the document
      if (id && document.getElementById(id)) continue;
      host.appendChild(element);
    }
  };

  /*
   * A <script> that arrives inside fetched markup NEVER executes - measured in
   * the page: innerHTML does not run it, and neither does appending a node
   * parsed by DOMParser (both false; custom elements DO still upgrade, so
   * <product-card> and <quick-buy-modal> are fine).
   *
   * product-card.liquid relies on that not being true: it registers its
   * event-delegated variant bar (the plus-button quick add) and its carousel
   * scroll indicator from inline scripts, guarded by
   * window.__sportscraftVariantBarInit / __sportscraftProductCardScrollIndicatorInit and
   * meant to run once "on first parse". On a Nosto page NO card is ever parsed
   * by the HTML parser, so those guards were never set and quick add did
   * nothing. Re-running each unique script body once through a script element
   * this file creates registers them exactly as a server-parsed card would.
   */
  const ranScripts = new Set();

  // Only these types are executable JavaScript. A product card also carries
  // <script type="application/json"> data blocks, and re-running one of those as
  // JavaScript throws "Unexpected token ':'" - three per page on a search for
  // "dress". Those blocks are read in place by the card's own code and travel
  // with the cloned card markup, so they must be skipped here, not copied.
  const JS_SCRIPT_TYPES = ['', 'text/javascript', 'application/javascript', 'module'];

  const runCardScripts = (doc) => {
    for (const source of doc.querySelectorAll('script')) {
      if (source.src) continue;
      const type = (source.getAttribute('type') || '').trim().toLowerCase();
      if (JS_SCRIPT_TYPES.indexOf(type) === -1) continue;
      const body = source.textContent || '';
      if (!body.trim() || ranScripts.has(body)) continue;
      ranScripts.add(body);
      const script = document.createElement('script');
      if (type === 'module') script.type = 'module';
      script.textContent = body;
      script.setAttribute('data-sportscraft-nosto-card-script', '');
      document.head.appendChild(script);
    }
  };

  // The wishlist app fills the heart (.wkh-button.wk-selected) from its own
  // state; assets/sportscraft-wishlist-products.js exposes this to force a re-render
  // after cards are injected, which is what makes saved hearts show up.
  const refreshWishlist = () => {
    if (typeof window.sportscraftWishlistRefresh === 'function') window.sportscraftWishlistRefresh();
  };

  /* --------------------------------------------------------- query builder */

  const gql = (value) => JSON.stringify(value);

  const parseSort = (sort) => {
    // Relevance is the absence of a sort argument. `_score` desc was verified
    // to return the identical order, so there is nothing to gain by sending it.
    if (!sort) return null;
    const at = String(sort).lastIndexOf(':');
    if (at < 1) return null;
    return { field: sort.slice(0, at), order: sort.slice(at + 1) === 'desc' ? 'desc' : 'asc' };
  };

  const buildSort = (sort) => {
    const parsed = parseSort(sort);
    if (!parsed) return '';
    return ', sort: [{ field: ' + gql(parsed.field) + ', order: ' + parsed.order + ' }]';
  };

  /*
   * The market product filter, resolved in Liquid from the shopper's Shopify
   * market (snippets/sportscraft-nosto-search.liquid). It is deliberately NOT part of
   * store.filters: it is not a shopper filter, so it must never appear in the
   * url, in the chips, or in the "you filtered down to nothing" state. Adding it
   * here instead puts it on EVERY request - results, category mode, and the
   * facet counts, which ride in the same `products` argument as `facets: ["*"]`.
   */
  const marketFilter = () => {
    const filter = config().marketFilter;
    if (!filter || !filter.field) return null;
    const values = (filter.value || []).filter((value) => value !== '' && value != null);
    return values.length ? { field: filter.field, value: values } : null;
  };

  const buildFilter = (filters, range) => {
    const parts = [];
    const market = marketFilter();
    if (market) {
      parts.push('{ field: ' + gql(market.field) + ', value: [' + market.value.map(gql).join(', ') + '] }');
    }
    for (const field of Object.keys(filters || {})) {
      const values = filters[field];
      if (!Array.isArray(values) || values.length === 0) continue;
      parts.push('{ field: ' + gql(field) + ', value: [' + values.map(gql).join(', ') + '] }');
    }
    if (range && (range.min != null || range.max != null)) {
      const inner = [];
      if (range.min != null) inner.push('gte: ' + gql(String(range.min)));
      if (range.max != null) inner.push('lte: ' + gql(String(range.max)));
      if (inner.length) {
        parts.push('{ field: ' + gql(PRICE_FIELD) + ', range: { ' + inner.join(', ') + ' } }');
      }
    }
    if (parts.length === 0) return '';
    return ', filter: [' + parts.join(', ') + ']';
  };

  const isWholeCatalogue = (state) => state.mode === 'category' &&
    (String(state.categoryId || '') === '' || String(state.categoryHandle || '') === 'all');

  const buildQuery = (state) => {
    // No `fields` argument: it is NOT defined on InputSearchProducts or
    // InputSearchKeywords any more (both 400 with "Input field 'fields' is not
    // defined on type ..."). The GraphQL selection set is what picks fields.
    const products = [
      'size: ' + Number(state.size),
      'from: ' + Number(state.from),
      'facets: ["*"]'
    ].join(', ');

    /*
     * Category mode drives the product set from products.categoryId and sends
     * no `query` and no `keywords` - Nosto returns the collection's products in
     * its merchandised order. The id must be the PLAIN NUMERIC Shopify
     * collection id: gid://shopify/Collection/<id> and the handle both return
     * 0 hits with no error at all (verified against the live endpoint).
     *
     * /collections/all is the exception. It is Shopify's virtual collection, so
     * there is no collection id to send, and the whole catalogue is the intended
     * product set. Nosto expresses that as NEITHER argument: no `query` and no
     * `categoryId` returns the full catalogue (measured: total 56, which is
     * exactly what /collections/all/products.json returns). Both of the
     * near-misses return 0 with no error, which is what made this look like an
     * empty collection - an empty `query` string (the old fallback here) and an
     * empty `categoryId` string. So the argument must be OMITTED, not blanked.
     */
    const isCategory = state.mode === 'category';
    const category = String(state.categoryId || '');
    const wholeCatalogue = isWholeCatalogue(state);

    let scope = '';
    if (isCategory) {
      if (!wholeCatalogue) scope = ', categoryId: ' + gql(category);
    }

    const inner = isCategory
      ? 'products: { ' + products + scope + buildSort(state.sort) + buildFilter(state.filters, state.range) + ' }'
      : 'products: { ' + products + buildSort(state.sort) + buildFilter(state.filters, state.range) + ' }, keywords: { size: 5 }';

    return 'query { search(' +
      'accountId: ' + gql(config().accountId) + ', ' +
      (isCategory ? '' : 'query: ' + gql(state.query || '') + ', ') +
      inner +
      ') { redirect ' +
        'products { total size from fuzzy hits { productId name url price listPrice imageUrl availability } ' +
          'facets { __typename ' +
            '... on SearchTermsFacet { id name field type data { value count selected } } ' +
            '... on SearchStatsFacet { id name field type min max } } } ' +
        (isCategory ? '' : 'keywords { hits { keyword } } ') + '} }';
  };

  // The plain-object form of what buildQuery sends, for api.recordSearch.
  const searchRequest = (state) => {
    const filter = [];
    const market = marketFilter();
    if (market) filter.push(market);
    for (const field of Object.keys(state.filters || {})) {
      const values = state.filters[field];
      if (Array.isArray(values) && values.length) filter.push({ field: field, value: values.slice() });
    }
    const range = state.range;
    if (range && (range.min != null || range.max != null)) {
      const bounds = {};
      if (range.min != null) bounds.gte = String(range.min);
      if (range.max != null) bounds.lte = String(range.max);
      filter.push({ field: PRICE_FIELD, range: bounds });
    }
    const products = { size: Number(state.size), from: Number(state.from) };
    const sort = parseSort(state.sort);
    if (sort) products.sort = [sort];
    if (filter.length) products.filter = filter;
    if (state.mode !== 'category') return { query: state.query || '', products: products };
    if (!isWholeCatalogue(state)) products.categoryId = String(state.categoryId);
    return { products: products };
  };

  /* ------------------------------------------------------------ hit tidy-up */

  // Nosto returns absolute urls on the suffixed myshopify domain; the theme
  // must link to the domain the shopper is actually on.
  const relativeUrl = (url) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname + parsed.search;
    } catch (error) {
      return url;
    }
  };

  const handleFromUrl = (url) => {
    const match = String(url || '').match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  };

  const normaliseHits = (hits) => (hits || []).map((hit) => Object.assign({}, hit, {
    url: relativeUrl(hit.url),
    handle: handleFromUrl(hit.url)
  }));

  /* ----------------------------------------------------------- card render */

  /*
   * One section request per chunk of up to 20 uncached handles, rendered by
   * sections/sportscraft-nosto-product-grid.liquid.
   *
   * The chunk url is built from the store's own handles, never from
   * location.search - a stray nf.* or filter.* param leaking into this request
   * would make Shopify filter the comma-joined handle search server-side.
   */
  const renderBatched = (grid, hits, run, existing, keptExtras, showPromos, isCurrent) => {
    const cfg = config();
    const chunkSize = Math.min(Number(cfg.chunkSize) || TRANSPORT_HANDLE_LIMIT, TRANSPORT_HANDLE_LIMIT);
    const kept = existing || new Map();
    const extras = keptExtras || new Map();

    // A card already on the page is moved into its Nosto position rather than
    // fetched again: on a collection page Shopify has server-rendered the whole
    // first slice, so most of the grid needs no request at all.
    const missing = [];
    for (const hit of hits) {
      if (!hit.handle || kept.has(hit.handle) || cardCache.has(hit.handle)) continue;
      if (missing.indexOf(hit.handle) === -1) missing.push(hit.handle);
    }

    const context = [];
    if (cfg.contextCollection) context.push('__ctx:' + cfg.contextCollection);

    const chunks = [];
    for (let i = 0; i < missing.length; i += chunkSize) {
      chunks.push({ offset: i, handles: missing.slice(i, i + chunkSize) });
    }

    /*
     * A chunk that answers with anything but the transport list contributes no
     * cards, and used to do so silently: the status was never read and the
     * catch was empty, so the run still reported success.
     */
    const fetchChunk = (url, run, attempt) => fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('status ' + response.status);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // a challenge or error page answers 200 with no transport in it
        if (!doc.querySelector('.sportscraft-nosto-grid-transport')) throw new Error('not the card transport');
        return doc;
      })
      .catch((failure) => {
        const reason = (failure && failure.message) || 'request failed';
        if (attempt === 0) {
          run.cardRequests += 1;
          return new Promise((resolve) => setTimeout(resolve, CHUNK_RETRY_MS))
            .then(() => fetchChunk(url, run, 1));
        }
        run.chunkFailures.push({ url: url, reason: reason });
        console.warn('[sportscraft-nosto-search] Card request failed (' + reason + '): ' + url);
        return null;
      });

    return Promise.all(chunks.map((chunk) => {
      // grid position drives the eager-first-3 image rule, so pass the position
      // this handle has in the CURRENT result list, not its position in the
      // uncached subset
      const from = hits.findIndex((hit) => hit.handle === chunk.handles[0]);
      const terms = ['__from:' + (from < 0 ? 0 : from)]
        .concat(context)
        .concat(chunk.handles)
        .join(',');
      const url = '/search?q=' + encodeURIComponent(terms) +
        '&type=product&section_id=' + encodeURIComponent(cfg.transportSection || 'sportscraft-nosto-product-grid');
      run.cardRequests += 1;
      return fetchChunk(url, run, 0)
        .then((doc) => {
          if (!doc) return;
          // register the card's delegated behaviour once (see runCardScripts)
          runCardScripts(doc);
          for (const item of doc.querySelectorAll('li[data-handle]')) {
            const card = item.querySelector('product-card');
            // the transport marks unresolved handles data-missing and emits no
            // card; belt and braces, only accept a card with a real handle
            if (item.hasAttribute('data-missing') || !card || !card.getAttribute('handle')) {
              run.placeholdersCaught += 1;
              continue;
            }
            // everything the card snippet renders BESIDE the card - today that
            // is the per-card wishlist drawer - is kept, not dropped
            const extras = [...item.children]
              .filter((el) => el !== card && el.tagName !== 'SCRIPT')
              .map((el) => el.outerHTML);
            cachePut(item.getAttribute('data-handle'), { card: card.outerHTML, extras: extras });
          }
        });
    })).then(() => {
      // Shopify's own cards are the same markup the transport returns, and this
      // swap is the last moment they exist: cached here, a return to page 1
      // costs no request instead of refetching the whole page.
      for (const [handle, card] of kept) {
        if (cardCache.has(handle)) continue;
        const extra = extras.get(extraIdFor(card));
        cachePut(handle, { card: card.outerHTML, extras: extra ? [extra.outerHTML] : [] });
      }
      /*
       * A superseded request keeps its cache entries but must not touch the
       * grid: an earlier run finishing last used to leave the previous query's
       * cards under the new query's count, chips, pagination and url.
       */
      if (isCurrent && !isCurrent()) {
        run.discarded = true;
        return;
      }
      const ordered = [];
      let fromServer = 0;
      for (const hit of hits) {
        // cards must be DIRECT children of .product-list: theme.css applies
        // `grid-template-rows: subgrid` to `.product-list > *`, so the <li>
        // transport wrapper is dropped here rather than injected
        const onPage = kept.get(hit.handle);
        if (onPage) {
          // the card's own wishlist drawer is moved out of the grid first, or
          // the rebuild below would destroy it and leave the heart inert
          const extra = extras.get(extraIdFor(onPage));
          if (extra) extrasHost().appendChild(extra);
          // appending a node that is already in the document MOVES it, which is
          // exactly what reordering Shopify's grid into Nosto's order needs
          ordered.push(onPage);
          fromServer += 1;
          continue;
        }
        const entry = cardFromCache(hit.handle);
        if (!entry) continue;
        ordered.push(entry.card);
        injectExtras(entry.extras);
      }
      run.cardsFromServer = fromServer;
      run.cardsFromCache = ordered.length - fromServer - missing.length;
      if (run.cardsFromCache < 0) run.cardsFromCache = 0;
      run.cardsInserted = ordered.length;
      run.cardsMissing = hits.length - ordered.length - run.placeholdersCaught;
      if (run.cardsMissing < 0) run.cardsMissing = 0;
      if (run.cardsMissing > 0) {
        console.warn('[sportscraft-nosto-search] ' + run.cardsMissing + ' of ' + hits.length +
          ' hits rendered no card (' + run.chunkFailures.length + ' failed card request(s))');
      }
      const children = showPromos ? withPromos(ordered) : ordered;
      run.promosInserted = children.length - ordered.length;
      const fragment = document.createDocumentFragment();
      for (const child of children) fragment.appendChild(child);
      // ONE swap, at the end: the server grid stays on screen until Nosto's
      // grid is ready to take its place, so a failed request leaves Shopify's
      // results intact instead of an empty grid.
      grid.replaceChildren(fragment);
      releaseServerGrid();
      refreshWishlist();
    });
  };

  /* ------------------------------------------------- the server-rendered grid */

  /*
   * Shopify's own cards, indexed by handle. On a collection or search page the
   * section renders the native grid into this same element (it is handed the
   * module's id), so these are the crawler baseline, the Shopify fallback AND
   * the cards the swap reuses.
   */
  const serverCards = (grid) => {
    const map = new Map();
    if (!grid) return map;
    for (const card of grid.querySelectorAll(':scope > product-card[handle]')) {
      map.set(card.getAttribute('handle'), card);
    }
    return map;
  };

  /*
   * product-card.liquid renders each card's "Save to wishlist" drawer as a
   * SIBLING of the card, so in Shopify's grid the drawer is another child of
   * <product-list>. Rebuilding the grid destroys those siblings, which left
   * every reused card's heart calling .show() on a drawer that no longer
   * existed (measured: 23 of 28 cards). They are indexed here by id so a reused
   * card can take its own drawer with it.
   */
  const serverExtras = (grid) => {
    const map = new Map();
    if (!grid) return map;
    for (const child of grid.children) {
      if (child.tagName !== 'PRODUCT-CARD' && child.id) map.set(child.id, child);
    }
    return map;
  };

  /*
   * Promo blocks - the section's image / video / "Sportscraft: Tile" blocks - are
   * children of Shopify's grid but are not product cards, so the rebuild
   * dropped them. Native placement, snippets/product-list.liquid line 68: a
   * block is emitted immediately before the product whose 1-based index equals
   * its `position` setting, and ONLY when the page is 1 and no filter is
   * active. Same rule here, with Nosto's chips standing in for the native
   * filters. The elements are kept and moved, never re-parsed, so a video tile
   * is not restarted on every state change.
   */
  const PROMO_SELECTOR = '.product-list__promo';
  const serverPromos = [];
  let promosCaptured = false;

  const capturePromos = (grid, page) => {
    if (promosCaptured || !grid || !serverGridOnScreen) return;
    serverPromos.length = 0;
    let cards = 0;
    for (const child of grid.children) {
      if (child.tagName === 'PRODUCT-CARD') cards += 1;
      // slot = how many cards precede it, so position 5 becomes slot 4
      else if (child.matches && child.matches(PROMO_SELECTOR)) serverPromos.push({ node: child, slot: cards });
    }
    // product-list.liquid renders the blocks on page 1 only, so latching on a
    // ?page=2 first load recorded "no tiles" for the whole session. `page` is
    // the page being requested, not the page the grid holds; they differ only
    // once the server grid has survived a request (the fallback path), and a
    // page-2 server grid carries no blocks to mis-attribute.
    if (page === 1) promosCaptured = true;
  };

  // A slot past the end of the page renders nothing, exactly as native does
  // when no product carries that index on the page.
  const withPromos = (cards) => {
    const out = cards.slice();
    // ascending slot so `inserted` keeps the later splice indexes valid, and a
    // stable sort so two blocks sharing a position keep the order the section
    // rendered them in
    let inserted = 0;
    for (const promo of serverPromos.slice().sort((a, b) => a.slot - b.slot)) {
      if (promo.slot >= cards.length) continue;
      out.splice(promo.slot + inserted, 0, promo.node);
      inserted += 1;
    }
    return out;
  };

  // the drawer id a card's wishlist trigger points at (data-drawer-id: the
  // trigger only gains aria-controls once its drawer exists, Productive #611)
  const extraIdFor = (card) => {
    const trigger = card.querySelector('.sportscraft-card-wishlist-trigger[data-drawer-id]');
    return trigger ? trigger.getAttribute('data-drawer-id') : '';
  };

  const hasServerCards = (grid) => !!(grid && grid.querySelector(':scope > product-card[handle]'));

  /*
   * Nosto's own cards are <product-card handle> too, so after the first swap
   * hasServerCards can no longer tell whose grid is on screen. This says
   * whether the grid is still exactly what the server rendered; it is the only
   * state the fallback is allowed to reveal, because Shopify's pagination in
   * the HTML is page 1's.
   */
  let serverGridOnScreen = true;
  const releaseServerGrid = () => {
    serverGridOnScreen = false;
  };

  /* ------------------------------------------------------------ the store */

  const storeDefinition = {
    query: '',
    filters: {},
    range: { min: null, max: null },
    priceBounds: { min: null, max: null },
    sort: '',
    page: 1,
    size: 24,
    total: 0,
    hits: [],
    facets: [],
    keywords: [],
    loading: false,
    error: false,
    // Shopify's own results are the ones on screen. Distinct from `error`: the
    // shopper is looking at a working product grid, so no error copy is shown.
    fallback: false,
    redirect: '',
    started: false,
    booted: false,
    ready: false,
    gateTimer: null,
    // request identity: only the newest search may write state or the grid
    runId: 0,

    get from() {
      return (this.page - 1) * this.size;
    },

    get pages() {
      if (!this.size) return 1;
      return Math.max(1, Math.ceil(this.total / this.size));
    },

    get termsFacets() {
      return this.facets.filter(
        (facet) => facet.__typename === 'SearchTermsFacet' && facet.field !== AVAILABILITY_FIELD
      );
    },

    get availabilityFacet() {
      return (
        this.facets.find(
          (facet) => facet.__typename === 'SearchTermsFacet' && facet.field === AVAILABILITY_FIELD
        ) || null
      );
    },

    get availabilityOn() {
      return (this.filters[AVAILABILITY_FIELD] || []).length > 0;
    },

    get availabilityLabel() {
      return (config().labels || {}).inStockOnly || '';
    },

    get priceFacet() {
      return this.facets.find((facet) => facet.__typename === 'SearchStatsFacet' && facet.field === PRICE_FIELD) || null;
    },

    get sortOptions() {
      return config().sortOptions || [];
    },

    get countLabel() {
      const labels = config().labels || {};
      const template = this.total === 1 ? labels.countOne : labels.countOther;
      return String(template || '').replace('__N__', this.total);
    },

    removeFilterLabel(value) {
      const labels = config().labels || {};
      return String(labels.removeFilter || '').replace('__V__', value);
    },

    /* ---- page number parts, mirroring the native pagination window ---- */

    get pageParts() {
      const parts = [];
      const pages = this.pages;
      const current = this.page;
      const want = new Set([1, pages, current, current - 1, current + 1]);
      let previous = 0;
      for (let p = 1; p <= pages; p++) {
        if (!want.has(p)) continue;
        if (previous && p - previous > 1) parts.push({ ellipsis: true, key: 'gap-' + p });
        parts.push({ page: p, current: p === current, key: 'page-' + p });
        previous = p;
      }
      return parts;
    },

    /* ------------------------------- chips ------------------------------- */

    get activeChips() {
      const chips = [];
      for (const field of Object.keys(this.filters)) {
        for (const value of this.filters[field] || []) {
          // availability reads as the toggle's own wording, not Nosto's "InStock"
          const label = field === AVAILABILITY_FIELD ? this.availabilityLabel : value;
          chips.push({ field: field, value: value, label: label, key: field + ':' + value, price: false });
        }
      }
      if (this.range.min != null || this.range.max != null) {
        const from = this.range.min == null ? this.priceBounds.min : this.range.min;
        const to = this.range.max == null ? this.priceBounds.max : this.range.max;
        chips.push({ field: PRICE_FIELD, value: from + ' - ' + to, label: from + ' - ' + to, key: 'price', price: true });
      }
      return chips;
    },

    removeChip(chip) {
      if (chip.price) this.clearPrice();
      else if (chip.field === AVAILABILITY_FIELD) this.toggleAvailability();
      else this.toggleFilter(chip.field, chip.value);
    },

    /* -------------------------------- URL -------------------------------- */

    readUrl() {
      const params = new URLSearchParams(window.location.search);
      // category mode has no ?q= - the product set comes from categoryId
      if (config().mode !== 'category') this.query = params.get('q') || '';
      this.page = Math.max(1, Number(params.get('page')) || 1);
      this.sort = params.get('sort') || '';

      const filters = {};
      for (const [key, value] of params.entries()) {
        if (key.indexOf(FACET_PARAM_PREFIX) !== 0) continue;
        const field = key.slice(FACET_PARAM_PREFIX.length);
        if (!field || !value) continue;
        filters[field] = value.split(',').filter(Boolean);
      }
      this.filters = filters;

      const price = params.get(PRICE_PARAM);
      if (price && price.indexOf('-') > -1) {
        const [min, max] = price.split('-');
        this.range = {
          min: min === '' ? null : Number(min),
          max: max === '' ? null : Number(max)
        };
      } else {
        this.range = { min: null, max: null };
      }
    },

    /*
     * Brief step 4 says replaceState, brief step 6 requires the back button to
     * restore a previous filter state. Those conflict: replaceState creates no
     * history entry, so popstate never fires for a filter step and back would
     * leave the page entirely. The tested outcome wins - user-initiated changes
     * push, and only the initial normalisation of the landing url replaces (so
     * arriving on the page does not leave a duplicate entry behind).
     */
    syncUrl(mode) {
      const params = new URLSearchParams(window.location.search);
      // drop this module's own params, then rewrite them, so a removed facet
      // actually leaves the url
      for (const key of [...params.keys()]) {
        if (key.indexOf(FACET_PARAM_PREFIX) === 0) params.delete(key);
      }
      params.delete(PRICE_PARAM);

      if (config().mode !== 'category') params.set('q', this.query);
      if (this.page > 1) params.set('page', String(this.page));
      else params.delete('page');
      if (this.sort) params.set('sort', this.sort);
      else params.delete('sort');

      for (const field of Object.keys(this.filters)) {
        const values = this.filters[field] || [];
        if (values.length) params.set(FACET_PARAM_PREFIX + field, values.join(','));
      }
      if (this.range.min != null || this.range.max != null) {
        params.set(PRICE_PARAM, (this.range.min == null ? '' : this.range.min) + '-' + (this.range.max == null ? '' : this.range.max));
      }

      const url = window.location.pathname + '?' + params.toString();
      if (mode === 'push') window.history.pushState({}, '', url);
      else window.history.replaceState({}, '', url);
    },

    /* ------------------------------ actions ------------------------------ */

    isChecked(field, value) {
      return (this.filters[field] || []).indexOf(value) !== -1;
    },

    /*
     * Binary control, so off clears the whole field rather than one value: a
     * url carrying OutOfStock would otherwise survive a state the toggle cannot
     * represent.
     */
    toggleAvailability() {
      if (this.availabilityOn) delete this.filters[AVAILABILITY_FIELD];
      else this.filters[AVAILABILITY_FIELD] = [AVAILABILITY_VALUE];
      this.page = 1;
      this.search({ writeUrl: 'push' });
    },

    toggleFilter(field, value) {
      const values = (this.filters[field] || []).slice();
      const at = values.indexOf(value);
      if (at === -1) values.push(value);
      else values.splice(at, 1);
      if (values.length) this.filters[field] = values;
      else delete this.filters[field];
      this.page = 1;
      this.search({ writeUrl: 'push' });
    },

    applyPrice(min, max) {
      const bounds = this.priceBounds;
      const toNumber = (raw) => (raw === '' || raw == null ? null : Number(raw));
      let low = toNumber(min);
      let high = toNumber(max);
      // an untouched range must not filter: a bound equal to the facet's own
      // bound is the same as no bound at all
      if (low != null && bounds.min != null && low <= bounds.min) low = null;
      if (high != null && bounds.max != null && high >= bounds.max) high = null;
      this.range = { min: low, max: high };
      this.page = 1;
      this.search({ writeUrl: 'push' });
    },

    clearPrice() {
      this.range = { min: null, max: null };
      this.page = 1;
      this.search({ writeUrl: 'push' });
    },

    clearFilters() {
      this.filters = {};
      this.range = { min: null, max: null };
      this.page = 1;
      this.search({ writeUrl: 'push' });
    },

    setSort(sort) {
      // 'relevance' is the dropdown's sentinel for "no sort argument"
      this.sort = sort === 'relevance' ? '' : sort;
      this.page = 1;
      this.search({ writeUrl: 'push' });
    },

    goToPage(page) {
      if (page < 1 || page > this.pages || page === this.page) return;
      this.page = page;
      this.search({ writeUrl: 'push' });
    },

    /* -------------------------------- boot ------------------------------- */

    // Alpine calls this itself when the store is registered. That can happen
    // while the document is still parsing (see the registration note at the
    // bottom of this file), in which case the grid element does not exist
    // yet - so the first search waits for the document.
    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.start(), { once: true });
      } else {
        this.start();
      }
    },

    start() {
      if (this.booted) return;
      this.booted = true;
      this.size = Number(config().size) || 24;
      if (bypassed()) {
        this.useFallback('bypassed by sportscraft_nosto=off');
        return;
      }
      this.readUrl();
      this.trackClicks();
      // history.replaceState never fires popstate, so this only ever runs for a
      // real back/forward step
      window.addEventListener('popstate', () => {
        this.readUrl();
        this.search({ writeUrl: false });
      });
      this.search();
    },

    // Delegated once: the grid element survives every replaceChildren swap.
    trackClicks() {
      const grid = document.getElementById('sportscraft-nosto-search-grid');
      if (!grid) return;
      grid.addEventListener('click', (event) => {
        if (this.fallback) return;
        const link = event.target.closest && event.target.closest('a[href*="/products/"]');
        if (!link) return;
        const handle = handleFromUrl(link.getAttribute('href'));
        const hit = handle && this.hits.find((item) => item.handle === handle);
        if (!hit) return;
        const type = config().mode === 'category' ? 'category' : 'serp';
        track((api) => api.recordSearchClick(type, { productId: hit.productId, url: new URL(hit.url, window.location.origin).href }));
      });
      // product-form dispatches variant:add only after cart/add.js succeeds
      document.addEventListener('variant:add', (event) => {
        if (this.fallback) return;
        const handle = addedHandle(event, grid);
        const hit = handle && this.hits.find((item) => item.handle === handle);
        if (!hit) return;
        const type = config().mode === 'category' ? 'category' : 'serp';
        track((api) => api.recordSearchAddToCart(type, { productId: hit.productId, url: new URL(hit.url, window.location.origin).href }));
      });
    },

    /* -------------------------------- gate ------------------------------- */

    /*
     * The reveal gate. The grid sits at opacity 0 behind
     * `.sportscraft-nosto-search:not(.sportscraft-nosto-ready)` (assets/sportscraft-nosto-search.css)
     * so a shopper never sees cards arrive one chunk at a time. Every exit path
     * calls openGate - cards inserted, zero hits, redirect, error - and armGate
     * adds a timer for the path that never returns, because a stalled request
     * would otherwise leave the grid permanently invisible. That trap is real:
     * the collection page's merchandising gate had exactly this shape and left
     * the Nosto grid blank when the snippet that opened it was replaced.
     */
    /*
     * The toolbar's Filters activator (snippets/collection-toolbar.liquid and
     * the floating button, lane t4-toolbar) opens the Nosto panel, so it must
     * not be offered when Nosto has sent no facets to put in it - including on
     * the fallback path, where the panel has nothing to show at all.
     */
    syncActivators() {
      const hasFacets =
        !this.fallback &&
        (this.termsFacets.length > 0 || !!this.priceFacet || !!this.availabilityFacet);
      for (const activator of document.querySelectorAll('[data-sportscraft-nosto-activator="filters"]')) {
        activator.hidden = !hasFacets;
      }
    },

    openGate() {
      if (this.gateTimer) {
        clearTimeout(this.gateTimer);
        this.gateTimer = null;
      }
      this.loading = false;
      this.ready = true;
      this.syncActivators();
      // The page-level zero-result composition (popular searches plus the
      // carousel in sections/main-search.liquid) keys off this class, so the
      // bands follow Nosto's count instead of Shopify's.
      // Only an UNFILTERED zero-result answer is "nothing matches your search".
      // Filtering down to nothing is a different state: the query still has
      // hits, so the page must keep its results chrome and offer a clear,
      // rather than telling the shopper their search found nothing.
      // Never on the fallback path: Shopify's results are on screen, so the
      // page must not also announce that nothing was found.
      document.documentElement.classList.toggle(
        'sportscraft-nosto-no-results',
        this.started && !this.error && !this.fallback && this.total === 0 && this.activeChips.length === 0
      );
    },

    /*
     * Native grids fade cards in through ProductList.reveal(); the theme's
     * "Reveal products one by one" setting and reduced-motion apply there, so
     * Nosto cards get the same treatment by borrowing it.
     */
    revealCards(grid) {
      const list = grid.closest('product-list') || grid;
      if (typeof list.reveal !== 'function') return;
      if (!window.matchMedia('(prefers-reduced-motion: no-preference)').matches) return;
      for (const card of grid.querySelectorAll('product-card, .product-list__promo')) {
        card.setAttribute('reveal-on-scroll', 'true');
      }
      list.reveal();
    },

    armGate() {
      this.ready = false;
      if (this.gateTimer) clearTimeout(this.gateTimer);
      this.gateTimer = setTimeout(() => {
        this.gateTimer = null;
        // the request that never settled is now superseded: the card requests
        // carry no abort signal, so without this a chunk resolving after the
        // backstop would swap Nosto cards in underneath Shopify's fallback grid
        this.runId += 1;
        this.useFallback('no response within ' + gateBackstopMs() + 'ms');
      }, gateBackstopMs());
    },

    /*
     * Shopify's own grid is what the shopper gets. Called for a timeout, a
     * network or GraphQL failure, an empty answer while Shopify has results,
     * and the support bypass. One warning, never an error state, as long as
     * there are real cards to fall back to; with no server grid there is
     * nothing to show, so the error copy stands.
     */
    useFallback(reason) {
      console.warn('[sportscraft-nosto-search] Showing Shopify results instead of Nosto: ' + reason);
      const grid = document.getElementById('sportscraft-nosto-search-grid');
      if (serverGridOnScreen && hasServerCards(grid)) {
        this.fallback = true;
        this.error = false;
      } else {
        // Nosto's own cards are on screen from an earlier page or sort. Keeping
        // them beats revealing Shopify's page-1 pagination over them, so the
        // grid is left alone and only the error copy is shown.
        this.error = true;
      }
      this.openGate();
    },

    /* ------------------------------- search ------------------------------ */

    search(options) {
      const urlMode = options && 'writeUrl' in options ? options.writeUrl : 'replace';
      const cfg = config();
      const run = startRun(this.size);
      this.runId += 1;
      const runId = this.runId;
      const isCurrent = () => runId === this.runId;
      const grid = document.getElementById('sportscraft-nosto-search-grid');

      // snapshot BEFORE anything touches the grid: these are Shopify's cards
      const existing = serverCards(grid);
      const existingExtras = serverExtras(grid);
      // and its promo blocks, while the grid is still the server's
      capturePromos(grid, this.page);

      this.loading = true;
      this.error = false;
      this.fallback = false;
      this.started = true;
      this.armGate();
      // Native reaches #collection on all of these: its pagination links carry
      // the hash and its facet form carries scroll-to (snippets/facets.liquid).
      // 'push' is exactly the shopper-driven steps - boot passes 'replace' and
      // back/forward passes false, where scroll restoration owns the position.
      if (urlMode === 'push') scrollToResults();
      // The grid is deliberately NOT emptied here. It holds Shopify's
      // server-rendered results, which are both the fallback and the cards the
      // swap reuses; clearing it up front is what made a failed request show an
      // empty grid. It is replaced in one step once Nosto has answered.

      const nostoStart = performance.now();
      performance.mark('sportscraft-nosto:request');

      const state = {
        mode: cfg.mode,
        categoryId: cfg.categoryId,
        categoryHandle: cfg.contextCollection,
        query: this.query,
        size: this.size,
        from: this.from,
        sort: this.sort,
        filters: this.filters,
        range: this.range
      };
      run.query = buildQuery(state);
      const request = searchRequest(state);
      const record = (search) => {
        const keyword = takeKeyword();
        if (cfg.mode === 'category') return track((api) => api.recordSearch('category', request, search));
        const isKeyword = !!keyword && keyword.trim().toLowerCase() === String(state.query || '').trim().toLowerCase();
        track((api) => api.recordSearch('serp', request, search, { isKeyword }));
      };

      return fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: run.query }),
        signal: AbortSignal.timeout ? AbortSignal.timeout(timeoutMs()) : undefined
      })
        /*
         * Decide on the PAYLOAD, never on the status or on `errors`.
         *
         * Nosto returns SOFT errors alongside completely valid data: a
         * merchandising pin rule plus an explicit sort answers HTTP 200 with
         * every hit present and
         * errors: [{ message: "removing parameter from search, reason: pins
         * provided with sort" }] - it dropped the pins and ran the sort. Those
         * hits must render. Measured live 9 Sep on "dress" with sort=price:asc:
         * HTTP 200, 15 hits, 1 error.
         *
         * And the status is not a reliable signal either way: the same account
         * answers HTTP 500 for an ai.* sort while still returning a body, and
         * HTTP 400 for a schema-invalid query. So the body is always parsed and
         * the only question asked of it is whether products came back.
         */
        .then((response) => response.json().catch(() => null))
        .then((payload) => {
          // a newer search has already answered: this one writes nothing
          if (!isCurrent()) {
            run.discarded = true;
            return;
          }
          const softErrors = (payload && payload.errors) || [];
          if (softErrors.length) {
            // logged, never fatal - see above
            console.warn('[sportscraft-nosto-search] Nosto reported:', softErrors.map((e) => e && e.message).join('; '));
          }

          const search = payload && payload.data && payload.data.search;
          // products null/missing is the real failure. An empty-but-present
          // products ({ total: 0, hits: [] }) is a legitimate no-results answer
          // and must fall through to the empty state, not the error state.
          if (!search || !search.products) {
            return Promise.reject(new Error('no products in response'));
          }

          run.nostoMs = performance.now() - nostoStart;
          performance.mark('sportscraft-nosto:response');

          // redirects are a search-engine concept; a category page has none
          if (search.redirect && cfg.mode !== 'category') {
            // same relativising the autocomplete's toPath does: a redirect
            // rule's target comes back on the suffixed myshopify domain
            this.redirect = relativeUrl(search.redirect);
            window.location.assign(this.redirect);
            return;
          }

          const products = search.products;
          this.total = products.total || 0;
          this.hits = normaliseHits(products.hits);
          this.facets = products.facets || [];
          this.keywords = ((search.keywords || {}).hits || []).map((hit) => hit.keyword);

          const price = this.priceFacet;
          if (price) {
            this.priceBounds = { min: Math.floor(price.min), max: Math.ceil(price.max) };
          }

          if (urlMode) this.syncUrl(urlMode);

          if (!grid || this.hits.length === 0) {
            // Nosto answering with nothing while Shopify has results is a
            // failure of the integration, not a real no-results page: show
            // Shopify's. Filtering down to nothing is different - the shopper
            // asked for it - so that keeps the module's own empty state and the
            // stale cards are cleared.
            if (this.hits.length === 0 && this.activeChips.length === 0 && serverGridOnScreen && hasServerCards(grid)) {
              this.useFallback('Nosto returned no results while Shopify has results');
              return;
            }
            record(search);
            if (grid) {
              grid.replaceChildren();
              releaseServerGrid();
            }
            this.openGate();
            return;
          }

          record(search);
          const cardsStart = performance.now();
          // native's own promo rule: page 1 only, and not while a filter is on
          const showPromos = this.page === 1 && this.activeChips.length === 0;
          return Promise.resolve(renderBatched(grid, this.hits, run, existing, existingExtras, showPromos, isCurrent)).then(() => {
            run.cardsMs = performance.now() - cardsStart;
            performance.mark('sportscraft-nosto:cards-done');
            performance.measure('sportscraft-nosto:cards', 'sportscraft-nosto:response', 'sportscraft-nosto:cards-done');
            // the newer run owns the gate; opening it here would reveal a grid
            // that is still mid-flight for that request
            if (isCurrent()) {
              this.revealCards(grid);
              this.openGate();
            }
          });
        })
        .catch((failure) => {
          if (!isCurrent()) {
            run.discarded = true;
            return;
          }
          this.useFallback((failure && failure.message) || 'request failed');
        });
    }
  };

  /*
   * Registration must not depend on script order. The shared Alpine loader
   * (snippets/sportscraft-nosto-alpine.liquid) appends its script with
   * document.createElement, and `defer` has NO effect on a dynamically
   * inserted script - it loads async and can execute at any time. So when
   * another section renders that loader earlier in the page (the autocomplete
   * lane renders it ~400 lines above this file's tag on the search page),
   * Alpine can boot and fire alpine:init before this file runs, and an
   * alpine:init-only listener never fires. Measured 9 Sep: the store did not
   * register at all and every binding in the shell threw.
   *
   * This is the crew-wide ruling in worker-rules.md ("Alpine store
   * registration"), not a local workaround.
   */
  const register = () => window.Alpine.store('nostoSearch', storeDefinition);
  if (window.Alpine && window.Alpine.store) register();
  else document.addEventListener('alpine:init', register, { once: true });
})();
